<template>
    <Layout disable-vertical-padding disable-horizontal-padding>
        <div v-bind:class="$style.container">
            <VFlex v-bind:class="$style.box">
                <form>
                    <header v-bind:class="$style.header">
                        <h2>Reset Password</h2>
                        <p v-bind:class="$style.subheader">
                            Already know your password?
                            <AppLink name="login" v-bind:class="$style.bold">
                                Login here
                            </AppLink>
                        </p>
                    </header>
                    <VDivider v-bind:class="$style.divider" />
                    <VTextField
                        v-model.trim="email"
                        type="email"
                        label="Email"
                        v-bind:error-messages="serverErrors.email || TextFieldValidation_getErrorMessages('email')"
                        required
                        v-on:blur="$v.email.$touch()"
                        v-on:keypress.enter="submit"
                    />
                    <VBtn
                        color="primary"
                        depressed
                        large
                        block
                        v-bind:loading="isSubmitting"
                        v-bind:class="$style.button"
                        v-on:click="submit"
                    >
                        Reset My Password
                    </VBtn>
                </form>
            </VFlex>
        </div>
    </Layout>
</template>

<script>
    import Layout from '@layouts/main';
    import TextFieldValidation from '@mixins/TextFieldValidation';
    import {
        required,
        email,
    } from 'vuelidate/lib/validators';

    export default {
        /**
         * Custom page meta data property for the view components.
         *
         * @type {Object}
         */
        metaInfo: {
            title: 'Password Reset',
            meta: [
                {
                    name: 'description',
                    content: 'Reset your Citizen J account password.',
                },
            ],
        },

        /**
         * Declaratively register required components.
         *
         * @link https://vuejs.org/v2/guide/components.html#Local-Registration
         */
        components: {
            Layout,
        },

        /**
         * Mixins are a flexible way to distribute reusable functionalities for Vue components.
         * A mixin object can contain any component options. When a component uses a mixin,
         * all options in the mixin will be “mixed” into the component’s own options.
         *
         * @link https://vuejs.org/v2/guide/mixins.html
         */
        mixins: [
            TextFieldValidation,
        ],

        /**
         * You must define defaults for all Vue models and reactive Vue template data.
         *
         * @link https://vuejs.org/v2/api/#data
         *
         * @return Object Vue model and binding defaults.
         */
        data () {
            return {
                email: null,
                serverErrors: false,
                isSubmitting: false,
            };
        },

        /**
         * Non-cached methods that are used by the Vue instance and template.
         *
         * @type Object ES6 methods
         */
        methods: {
            submit () {
                // Verify that the form is valid
                if (this.$v.$invalid) {
                    return this.$v.$touch();
                }

                // Set reactive state defaults
                this.isSubmitting = true;

                this.axios
                    .post('/auth/reset-password', {
                        email: this.email,
                    })
                    .then(() => this.$router.push('/login?reset=true'))
                    .catch(({ response }) => this.serverErrors = response.data.errors || {})
                    .then(() => this.isSubmitting = false);
            },
        },

        /**
         * Define custom validation rules on reactive data for Vuelidate validation.
         *
         * @link https://monterail.github.io/vuelidate
         *
         * @type {Object}
         */
        validations: {
            email: {
                required,
                email,
            },
        },
    };
</script>

<style lang="scss" module>
    @import '@design';

    .box {
        @apply px-10 pt-12 pb-8 bg-white rounded-lg;
    }

    .header {
        @apply text-center;
    }

    .subheader {
        @apply mt-4;
    }

    .divider {
        @apply my-6;
    }

    .container {
        // This needs to remain nested to overide Vuetify button margins
        .button {
            @apply my-6;
        }
    }

    .bold {
        @apply font-bold;
    }

    @screen sm {
        .container {
            @apply px-6 py-12 flex items-center justify-center;

            // This needs to remain nested to overide Vuetify flex class max-width
            .box {
                max-width: 42rem;
            }
        }
    }
</style>
